import React from "react";
import { useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import {
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
} from "@material-ui/core";
import {
  Cancel as CancelIcon,
  CheckCircleOutline,
  ErrorOutline,
  Menu,
  PlayArrow,
  Publish as PublishIcon,
  Undo as UndoIcon,
  SwapHoriz,
  SwapVert,
  Save as SaveIcon,
  WarningRounded,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { useStyles } from "../FlowBuilder/flowEditorStyles";
import { FLOW_CHANNEL_META, channelOfFlow } from "../../constants/flowChannels";

const FlowToolbar = ({
  flowName,
  setFlowName,
  nodes,
  hasUnsavedChanges,
  saving,
  onSave,
  onCancel,
  onOpenDrawer,
  provider,
  lintResult,
  onToggleLintPanel,
  onPreview,
  previewDisabled,
  previewActive,
  onAutoLayout,
  isDraft,
  onPublish,
  onDiscardDraft,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.down("sm"));
  const canal = FLOW_CHANNEL_META[channelOfFlow(provider)];

  return (
    <Toolbar className={classes.toolbar}>
      <Box display="flex" alignItems="center" style={{ gap: 8, flex: 1, minWidth: 0 }}>
        {isMobile && (
          <Tooltip title="Nodos">
            <IconButton
              size="small"
              onClick={onOpenDrawer}
              style={{ flexShrink: 0 }}
            >
              <Menu style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <TextField
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          placeholder={i18n.t("flowbuilders.editor.newFlow") || "Nuevo Flujo"}
          variant="outlined"
          size="small"
          style={{
            minWidth: isMobile ? "auto" : isTablet ? 180 : 300,
            flex: isMobile ? 1 : undefined,
            backgroundColor: theme.palette.background.paper,
          }}
          InputProps={{
            style: {
              fontWeight: 600,
              fontSize: isMobile ? 14 : 16,
            },
          }}
        />
        {nodes.length > 0 && !isMobile && (
          <Chip
            label={`${nodes.length} nodo${nodes.length > 1 ? "s" : ""}`}
            size="small"
            style={{
              backgroundColor: theme.palette.type === "dark"
                ? theme.palette.primary.dark
                : "#e3f2fd",
              color: theme.palette.type === "dark"
                ? theme.palette.primary.light
                : "#1976d2",
              fontWeight: 600,
              flexShrink: 0,
            }}
          />
        )}
        {provider && !isMobile && (
          <Tooltip
            title={`Flujo de ${canal.long}. ${canal.description}`}
          >
            <Chip
              avatar={<img src={canal.icon} alt="" style={{ width: 16, height: 16, marginLeft: 4 }} />}
              label={`Canal: ${canal.label}`}
              size="small"
              style={{
                backgroundColor: canal.color,
                color: "#fff",
                fontWeight: 600,
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box display="flex" alignItems="center" style={{ gap: 4, flexShrink: 0 }}>
        {lintResult && nodes.length > 0 && (
          <Tooltip
            title={
              lintResult.errors.length > 0
                ? i18n.t("flowbuilders.validation.errorsLabel", {
                    count: lintResult.errors.length,
                  })
                : lintResult.warnings.length > 0
                ? i18n.t("flowbuilders.validation.warningsLabel", {
                    count: lintResult.warnings.length,
                  })
                : i18n.t("flowbuilders.validation.allGood") ||
                  "El flujo no presenta problemas"
            }
          >
            <IconButton size="small" onClick={onToggleLintPanel}>
              {lintResult.errors.length > 0 ? (
                <ErrorOutline
                  style={{ color: theme.palette.error.main, fontSize: 22 }}
                />
              ) : lintResult.warnings.length > 0 ? (
                <WarningRounded
                  style={{ color: theme.palette.warning.main, fontSize: 22 }}
                />
              ) : (
                <CheckCircleOutline
                  style={{ color: theme.palette.success.main, fontSize: 22 }}
                />
              )}
            </IconButton>
          </Tooltip>
        )}
        {/* 0422 R4: guardado pero sin publicar — el motor aún usa la versión anterior */}
        {isDraft && !hasUnsavedChanges && !isMobile && (
          <Tooltip title="Los cambios están guardados, pero las conversaciones siguen con la versión publicada">
            <Chip
              label="Borrador sin publicar"
              size="small"
              style={{ backgroundColor: "#1e88e5", color: "#fff", marginRight: 4 }}
            />
          </Tooltip>
        )}
        {hasUnsavedChanges && !isMobile && (
          <Chip
            label="Sin guardar"
            size="small"
            style={{ backgroundColor: "#ff9800", color: "#fff", marginRight: 4 }}
          />
        )}
        {/**
          * Autoajuste (0419 F3-UX): un flujo de 27 nodos es ilegible tal y como el editor deja
          * los recuadros, y colocarlos a mano hay que repetirlo cada vez que se anade una rama.
          * Dos direcciones porque no hay una buena para todo: en vertical se leen los flujos
          * cortos, en horizontal los que tienen menus anchos.
          */}
        {onAutoLayout && (
          <>
            <Tooltip title="Autoajustar en vertical">
              <IconButton
                size="small"
                onClick={() => onAutoLayout("vertical")}
                style={{ marginRight: 2 }}
              >
                <SwapVert style={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Autoajustar en horizontal">
              <IconButton
                size="small"
                onClick={() => onAutoLayout("horizontal")}
                style={{ marginRight: 4 }}
              >
                <SwapHoriz style={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {onPreview && (
          <Tooltip
            title={
              lintResult?.hasBlockingErrors
                ? i18n.t("flowbuilders.validation.blockedPreview") ||
                  "Corrige los errores antes de previsualizar el flujo"
                : i18n.t("flowbuilders.simulator.title") || "Previsualizar bot"
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={onPreview}
                disabled={previewDisabled || previewActive}
                style={{ marginRight: 4 }}
              >
                <PlayArrow
                  style={{
                    fontSize: 24,
                    color: previewDisabled
                      ? theme.palette.action.disabled
                      : theme.palette.success.main,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {isDraft && onDiscardDraft && (
          <Tooltip title="Descartar borrador (volver a lo publicado)">
            <IconButton size="small" onClick={onDiscardDraft} style={{ marginRight: 4 }}>
              <UndoIcon style={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        )}
        {onPublish && (
          <Tooltip title="Publicar: las conversaciones pasan a usar esta versión">
            <span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<PublishIcon />}
                onClick={onPublish}
                disabled={saving || !(isDraft || hasUnsavedChanges)}
                style={{ marginRight: 8 }}
              >
                {isMobile ? "" : "Publicar"}
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title={onPublish ? "Guardar borrador" : (i18n.t("flowbuilders.editor.save") || "Guardar")}>
          <IconButton
            color="primary"
            onClick={onSave}
            disabled={saving}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "white",
            }}
          >
            <SaveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={i18n.t("flowbuilders.editor.cancel") || "Cerrar"}>
          <IconButton
            onClick={onCancel}
            style={{ color: theme.palette.text.secondary }}
          >
            <CancelIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Toolbar>
  );
};

export default FlowToolbar;
